<template>
  <div
    class="overlay"
    aria-modal="true"
    role="dialog"
    :hidden="!isVisible"
    tabindex="-1"
  >
    <div
      v-show="loaders.payment"
      class="blocker"
      style="position: absolute; top:0; left:0; z-index: 20000;"
    />
    <transition
      v-show="isVisible"
      :name="modalSettings.transition"
    >
      <div v-show="isVisible" class="payment-modal">
        <div class="payment-modal__container">
          <button
            class="payment-modal__close"
            :disabled="loaders.payment"
            @click="onClose"
          >
            <nuxt-icon name="close" />
          </button>
          <div class="payment-modal__content">
            <div class="payment-modal__header">
              <h1
                v-if="title"
                :class="[
                  'h2_title text-center',
                  { 'payment-modal__title': !showDescription },
                  locale,
                ]"
              >
                {{ $t('lp_v7_pop_up_title') }}
              </h1>
            </div>
            <component
              :is="formComponent"
              :key="randomId"
              :configs="paymentData"
              :params="paymentParams"
              :upsale="upsale"
              :show-description="showDescription"
              :stage="stage"
              :is-super-discount="isSuperDiscount"
              :selected-plan-id="selectedPlanId"
              :theme="theme"
              :theme-classes="themeClasses"
              @on-success="onSuccess"
              @action="emit('action')"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import type { BasePaymentModel, PaymentModel } from '~/common/constants/SubscriptionPlans.ts';
import { generateId } from '~/utils/generateId.ts';

interface ModalSettings {
  transition: string
  selfClick: boolean
}

interface Props {
  form: string
  title?: boolean
  showDescription?: boolean
  upsale?: boolean
  stage?: string
  selectedPlanId?: number
  isSuperDiscount?: boolean
  theme?: string
  paymentData: PaymentModel
  paymentParams?: BasePaymentModel
  modalSettings?: ModalSettings
  themeClasses?: { discount: string, regular: string }
}

const props = withDefaults(defineProps<Props>(), {
  form: 'BasicCheckoutForm',
  title: true,
  showDescription: true,
  upsale: false,
  stage: '',
  theme: '',
  isSuperDiscount: false,
  modalSettings: () => ({
    transition: 'slide-bottom',
    selfClick: true,
  }),
  themeClasses: () => ({ discount: 'red', regular: 'blue' }),
});

const emit = defineEmits(['action', 'closed', 'on-success']);
const store = useKegelStore();
const { isVisible, showCheckout } = useCheckoutModal();
const { loaders } = useLoader();
const { locale } = useI18n();
const randomId = ref(generateId());
const formComponent = computed(() => defineAsyncComponent(() => import(`../payment/checkoutForms/${props.form}.vue`)));
function onClose() {
  showCheckout();
  emit('closed');
}
function onSuccess() {
  emit('on-success');
  emit('action');
}

watch(isVisible, (value) => {
  if (!value) {
    randomId.value = generateId();
  }
});

onBeforeUnmount(() => {
  showCheckout(false);
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/animations.scss';

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 101;

  background: rgba(17, 17, 19, 0.75);

  overflow: scroll;

  &::-webkit-scrollbar {
    display: none;
  }

  &.no-touch {
    pointer-events: none;
  }
}

.payment-modal {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;

  &__header {
    @media (max-width: 400px) {
      & h1 {
        font-size: 18px;
      }
      & .en {
        font-size: 20px;
      }
    }
  }

  &__container {
    position: relative;
    max-width: 600px;
    margin: 0 auto;
    background: $white;
    border-radius: 16px 16px 0 0;
    padding: 24px 0 100px;
    height: 95dvh;
    overflow-y: scroll;
  }

  &__title {
    margin-bottom: 16px;
  }

  &__close {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: transparent;
    border: none;

    & .nuxt-icon {
      color: $icon-gray;
      line-height: 0;
      font-size: 32px;
    }
  }
}

.hidden {
  opacity: 0;
  z-index: -1;
}

.blocker {
  min-height: 100vh;
  min-width: 100vw;
}
</style>
